import { useCombobox } from "downshift";
import {
    CSSProperties,
    MutableRefObject,
    ReactNode,
    forwardRef,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import { ProgressCircular } from "../Progress/ProgressCircular";
import { Thumbnail } from "../Thumbnail";
import styles from "./ProductSearchAutocomplete.module.scss";

export type ProductSearchOption = {
    /** Stable id (product id). */
    value: string;
    /** Visible product name. */
    label: string;
    /** Optional product image URL. */
    imageUrl?: string;
    disabled?: boolean;
};

export type ProductSearchAutocompleteProps = {
    /** Options supplied by the parent (already filtered remotely). */
    options: ProductSearchOption[];
    /** Called on every input change — parent drives remote search. */
    onQueryChange?: (query: string) => void;
    /** Controlled selected option value. */
    value?: string | null;
    /** Uncontrolled default. */
    defaultValue?: string | null;
    /** Fires when the user picks an option (value = option.value). */
    onChange?: (value: string | null) => void;
    /** Shows a spinner in the search icon slot. */
    loading?: boolean;
    disabled?: boolean;
    /** Placeholder inside the field. Default: “Search for a product”. */
    placeholder?: string;
    /** Section title above the result list. Default: “Products”. */
    listTitle?: string;
    /** Empty list copy. Default: “No products”. */
    emptyMessage?: ReactNode;
    helperText?: string;
    error?: boolean | string;
    fullWidth?: boolean;
    /**
     * When `true` (default), menu portals to `document.body`.
     * Use `false` inside dialogs/modals.
     */
    portal?: boolean;
    id?: string;
    className?: string;
    name?: string;
};

const useMenuPosition = (
    fieldRef: MutableRefObject<HTMLDivElement | null>,
    isOpen: boolean,
): CSSProperties => {
    const [style, setStyle] = useState<CSSProperties>({
        position: "fixed",
        top: "-9999px",
        left: "-9999px",
        width: "0px",
    });

    useLayoutEffect(() => {
        if (!isOpen) {
            setStyle({
                position: "fixed",
                top: "-9999px",
                left: "-9999px",
                width: "0px",
            });
            return;
        }
        const update = () => {
            const el = fieldRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            setStyle({
                position: "fixed",
                top: `${rect.bottom + 4}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
            });
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [fieldRef, isOpen]);

    return style;
};

const useIsBrowser = () => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    return mounted;
};

const SearchIcon = () => <Icon icon="search" />;
const ChevronIcon = () => <Icon icon="chevron-down" />;

/**
 * Treez DS — Product search combobox for remote catalogs (e.g. Orders Add product).
 *
 * Parent owns search (`onQueryChange` + `options`). This component does **not**
 * filter options locally — it renders whatever the parent passes.
 */
export const ProductSearchAutocomplete = forwardRef<
    HTMLInputElement,
    ProductSearchAutocompleteProps
>(function ProductSearchAutocomplete(
    {
        options,
        onQueryChange,
        value,
        defaultValue,
        onChange,
        loading = false,
        disabled = false,
        placeholder = "Search for a product",
        listTitle = "Products",
        emptyMessage = "No products",
        helperText,
        error,
        fullWidth = true,
        portal = true,
        id,
        className,
        name,
    },
    ref,
) {
    const reactId = useId();
    const fieldId = id ?? `ds-product-search-${reactId}`;
    const messageId = `${fieldId}-msg`;

    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(
        defaultValue ?? null,
    );
    const selectedValue = isControlled ? value ?? null : uncontrolledValue;

    const selectedOption =
        selectedValue != null
            ? options.find(o => o.value === selectedValue) ?? null
            : null;

    const [inputValue, setInputValue] = useState(selectedOption?.label ?? "");

    const {
        isOpen,
        getMenuProps,
        getInputProps,
        getItemProps,
        getToggleButtonProps,
        highlightedIndex,
        openMenu,
    } = useCombobox<ProductSearchOption>({
        items: options,
        inputValue,
        selectedItem: selectedOption,
        itemToString: item => item?.label ?? "",
        // Parent already filtered — do not re-filter locally.
        stateReducer: (_state, { changes, type }) => {
            if (type === useCombobox.stateChangeTypes.InputClick) {
                return { ...changes, isOpen: true };
            }
            return changes;
        },
        onInputValueChange: ({ inputValue: next, type }) => {
            const nextValue = next ?? "";
            setInputValue(nextValue);
            if (type === useCombobox.stateChangeTypes.InputChange) {
                onQueryChange?.(nextValue);
            }
        },
        onSelectedItemChange: ({ selectedItem }) => {
            const nextValue = selectedItem?.value ?? null;
            if (!isControlled) setUncontrolledValue(nextValue);
            onChange?.(nextValue);
            setInputValue(selectedItem?.label ?? "");
        },
    });

    const hasError = Boolean(error);
    const message = typeof error === "string" ? error : helperText;

    const fieldRef = useRef<HTMLDivElement | null>(null);
    const menuStyle = useMenuPosition(fieldRef, isOpen);
    const isBrowser = useIsBrowser();

    const menuNode = (
        <div
            className={cx(styles.menu, isOpen && styles.menuOpen)}
            style={menuStyle}
            {...getMenuProps({
                onMouseDown: event => event.preventDefault(),
            })}
            data-ui="product-search-autocomplete-menu"
        >
            {isOpen ? (
                <>
                    <p className={styles.menuHeader}>{listTitle}</p>
                    <div className={styles.menuBody}>
                        {options.length === 0 ? (
                            <p className={styles.empty}>{emptyMessage}</p>
                        ) : (
                            <ul className={styles.list}>
                                {options.map((opt, index) => {
                                    const itemProps = getItemProps({
                                        item: opt,
                                        index,
                                        disabled: opt.disabled,
                                    });
                                    return (
                                        <li
                                            {...itemProps}
                                            className={cx(
                                                styles.item,
                                                highlightedIndex === index &&
                                                    styles.itemHighlighted,
                                                selectedOption?.value ===
                                                    opt.value &&
                                                    styles.itemSelected,
                                                opt.disabled &&
                                                    styles.itemDisabled,
                                            )}
                                            key={opt.value}
                                        >
                                            <Thumbnail
                                                alt=""
                                                bordered
                                                size="md"
                                                src={opt.imageUrl}
                                                variant="product-image"
                                            />
                                            <span className={styles.itemLabel}>
                                                {opt.label}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </>
            ) : null}
        </div>
    );

    return (
        <div
            className={cx(
                styles.root,
                fullWidth && styles.fullWidth,
                className,
            )}
            data-disabled={disabled || undefined}
            data-error={hasError || undefined}
            data-state={isOpen ? "open" : undefined}
            data-ui="product-search-autocomplete"
        >
            <div
                className={styles.field}
                onClick={() => {
                    if (!disabled) openMenu();
                }}
                ref={fieldRef}
            >
                <span aria-hidden className={styles.searchIcon}>
                    {loading ? (
                        <ProgressCircular
                            aria-label="Searching"
                            className={styles.searchProgress}
                            indeterminate
                            size="sm"
                        />
                    ) : (
                        <SearchIcon />
                    )}
                </span>
                <input
                    aria-describedby={message ? messageId : undefined}
                    aria-invalid={hasError || undefined}
                    className={styles.input}
                    disabled={disabled}
                    id={fieldId}
                    name={name}
                    placeholder={placeholder}
                    {...getInputProps({ ref })}
                />
                <button
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                    className={styles.toggle}
                    disabled={disabled}
                    type="button"
                    {...getToggleButtonProps()}
                >
                    <ChevronIcon />
                </button>
            </div>

            {portal && isBrowser
                ? createPortal(menuNode, document.body)
                : menuNode}

            {message ? (
                <div className={styles.helper} id={messageId}>
                    {message}
                </div>
            ) : null}
        </div>
    );
});

ProductSearchAutocomplete.displayName = "ProductSearchAutocomplete";
